
import * as React from 'react';
import {
	BarChart,
	Bar,
	Cell,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer
} from 'recharts';

import * as moment from "moment";
import { Moment } from "moment";

import { safe, groupBy, isNullOrUndefined, deepCopy, parseToRoundedDecimal, generateRandomKey } from "../../utils";
import { IPageProperties } from "../../models";
import { BackToTop } from "../../assets/BackToTop/BackToTop";
import { Disclaimer } from "../../assets";
import { DataTableRow } from "./DataTableRow";
import { VerticalBarChart } from "./VerticalBarChart";
import { StackedBarChart } from './StackedBarChart';

const colours = ['#0a4651', '#c8c73c', "#437497", "#363636", "#ccc9c9", "#2d2d2d", "#21616c", "#e3e6e7"];
const styles = require('./styles.pcss');
const backToTopstyles = require('./overrides/BackToTopoverride.pcss');

interface IState {
	isFixedIncome?: boolean;
	isMultiAsset?: boolean;
	startDate?: Moment;
	displayTypes?: {
		table: string[];
		tableConfig?: {
			[property: string]: {
				headings: string[];
				keys: string[];
			}
		},
		verticalChart: string[];
		horizontalChart: string[];
	};
}

export class PageAllocations extends React.Component<IPageProperties, IState> {
	constructor(props) {
		super(props);
		this.state = {
			isFixedIncome: false,
			isMultiAsset: false,
			startDate: moment(),
			displayTypes: {
				table: [],
				tableConfig: {},
				verticalChart: [],
				horizontalChart: []
			}
		};

		this.setReportMonth = this.setReportMonth.bind(this);
		this.getGroupedBreakdowns = this.getGroupedBreakdowns.bind(this);
		this.groupBreakdowns = this.groupBreakdowns.bind(this);
		this.getVerticalBarChart = this.getVerticalBarChart.bind(this);
	}

	componentWillMount() {
		const { fund } = this.props;
		const isFixedIncome = (fund.properties.asset_class as string).toLowerCase() === "fixed income";
		const isMultiAsset = (fund.properties.asset_class as string).toLowerCase() === "multi-asset";

		let displayTypes = {
			table: [],
			tableConfig: {},
			verticalChart: [],
			horizontalChart: []
		};

		let breakdowns = this.getGroupedBreakdowns();

		// Describes Allocations that are meant to be rendered as a chart...
		let verticalChart = [];
		if (!isFixedIncome) {
			verticalChart.push("GEOGRAPHIC");
		}
		displayTypes.verticalChart = [
			...verticalChart,
			// "TOP_HOLDINGS",
			// "EXPOSURE_POSITIVE",
			// "EXPOSURE_NEGATIVE",
			"CAPITAL",
			"CURRENCY",
			"CREDIT_RATING",
			"GEOGRAPHIC",
			// "TOP_ISSUERS",
			"SECTOR",
			"ASSET",
			"ASSET_GEOGRAPHIC",
		];
		displayTypes.table = ["MATURITY"];

		for (let key in breakdowns) {
			if (breakdowns[key].length > 0) {
				const allocationType = (breakdowns[key][0].allocation_type as string).toUpperCase();
				if (!displayTypes.horizontalChart.includes(allocationType) && !displayTypes.verticalChart.includes(allocationType)) {
					const headings = (breakdowns[key][0].table_headings as string).split("|");
					const keys = (breakdowns[key][0].table_keys as string).split("|");
					displayTypes.table.push(allocationType);
					if (!displayTypes.tableConfig[allocationType]) {
						displayTypes.tableConfig[allocationType] = {
							headings: [],
							keys: []
						};
					}
					displayTypes.tableConfig[allocationType].headings.push("", ...headings);
					displayTypes.tableConfig[allocationType].keys.push("label", ...keys);
				}
			}
		}

		this.setState({ displayTypes, isFixedIncome, isMultiAsset });
	}

	setReportMonth(date) {
		this.setState({ startDate: date });
	}

	getGroupedBreakdowns() {
		const { fund, appParams } = this.props;
		const { clienttype } = appParams;

		let breakdowns = safe(() => fund.allocations.filter(item => item.code === "breakdowns")[0].values, []);
		let groupedBreakdowns = groupBy(breakdowns, (item) => item["display_order"] as string);

		Object.entries(groupedBreakdowns).map(([key, breakdown]) => {
			// Remove breakdowns that have clientType specified and matches the current clientType
			if (breakdown.length > 0 && breakdown[0].client_type) {
				groupedBreakdowns[key] = breakdown.filter(item => item.client_type === clienttype);
			}
			else {
				groupedBreakdowns[key] = breakdown;
			}
		});

		return groupedBreakdowns;
	}

	groupBreakdowns() {
		let { fund } = this.props;
		let breakdowns = fund.allocations.filter(item => item.code === "breakdowns")[0].values;
		let groupedBreakdowns = groupBy(breakdowns, (item) => item["display_order"] as string);
		let subGroupedBreakdowns = {};
		for (let key in groupedBreakdowns) {
			let lastGroupedBreakdowns = {};
			let allocationType = groupedBreakdowns[key][0]["allocation_type"];
			let groupedByKey = groupBy(groupedBreakdowns[key], (item) => item["group_name"] as string);
			for (let groupedKey in groupedByKey) {
				let lastGroup = groupBy(groupedByKey[groupedKey], (item) => item["type"] as string);
				Object.assign(lastGroupedBreakdowns, { [groupedKey]: lastGroup, allocation_type: allocationType });
				for (let key in lastGroupedBreakdowns[groupedKey]) {
					lastGroupedBreakdowns[groupedKey][key].sort((a, b) => a.order - b.order);
				}
			}
			Object.assign(subGroupedBreakdowns, { [key]: lastGroupedBreakdowns });
		}

		return subGroupedBreakdowns;
	}

	filterOrderData(data) {
		let itemData = [
			...data.filter(item => this.parseAllocationValue(item, "ITEM")),
			...data.filter(item => this.parseAllocationValue(item, "AVERAGE"))
		];
		let newData = {
			total: data.filter(item => this.parseAllocationValue(item, "TOTAL")).sort((a, b) => a["order"] - b["order"]),
			item: []
		};

		//Pull out a unique list of groups and order by group....
		const groups = new Set(itemData.map(x => x.group_name));
		groups.forEach(groupName => {
			newData.item = newData.item.concat(itemData.filter(x => x.group_name === groupName).sort((a, b) => a["order"] - b["order"]));
		});

		for (let key in newData) {
			newData[key].map;
		}

		return newData;
	}

	parseAllocationValue(item, type) {
		item["value"] = parseFloat(item["allocation_value"]);
		return item["type"] === type;
	}

	getVerticalBarChart(data, shareClass, appParams) {
		const { isFixedIncome, isMultiAsset } = this.state;
		const newData = deepCopy(this.filterOrderData(data));
		return (
			<VerticalBarChart
				key={generateRandomKey("verticlBarChart_", 8)}
				newData={newData}
				isMultiAsset={isMultiAsset}
				isFixedIncome={isFixedIncome}
				shareClass={shareClass}
				appParams={appParams}
			/>
		);
	}

	getStackedChart(data, shareClass, appParams) {
		const { isFixedIncome, isMultiAsset } = this.state;
		const newData = deepCopy(this.filterOrderData(data));
		return (
			<StackedBarChart
				newData={newData}
				isMultiAsset={isMultiAsset}
				isFixedIncome={isFixedIncome}
				shareClass={shareClass}
				appParams={appParams}
			/>
		);
	}

	getHorizontalChart(data, shareClass, appParams) {
		let dataMaturity = this.filterOrderData(data);
		let transformedData = dataMaturity.item.map((item, imdex) => {
			item["Physical"] = item.value;
			return item;
		});
		return (
			<div key={data[0].display_order * 5} className={`${styles.colXs12} ${styles.colMd12}`}>
				<h4>{data[0]["heading"]}</h4>
				<div className={styles.barChart}>
					<ResponsiveContainer>
						<BarChart data={transformedData} margin={{ top: 15, right: 0, left: 0, bottom: 0 }}>
							<XAxis dataKey="label" tickLine={false} />
							<YAxis />
							<CartesianGrid vertical={false} strokeWidth={2} stroke="#e6e8e9" />
							<Tooltip />
							<Bar dataKey="Physical" fill="#0a4651">
								{transformedData.map((entry, i) => <Cell key={i * 58} fill={colours[i % colours.length]} />)}
							</Bar>
						</BarChart>
					</ResponsiveContainer>
				</div>
				<Disclaimer appParams={appParams} disclaimer={shareClass.disclaimers[`breakdown_definition_${data[0].allocation_type}_${appParams.clienttype}_0`]}></Disclaimer>
				<br />
			</div>
		);
	}

	getDataTable(config, data, shareClass, appParams, allocationType) {
		let newData = this.filterOrderData(data);
		if (allocationType === "sector" || allocationType === "asset_geographic") {
			return (
				<div key={data[0].display_order * 103943} className={`${styles.colXs12} ${styles.colMd6} ${styles.hasGroupHeading}`}>
					<h4>{data[0].heading}</h4>
					<div className={styles.toggleButtonContainer}>
					{/* No toggle buttons on table but this is needed to fill the space*/}
					</div>
					<table className={styles.table}>
						<thead>
							<tr>
								{config.headings.map((item, index) => (
									<th key={index * 9}>{item}</th>
								))}
							</tr>
						</thead>
						<tbody>
							{this.getGroupedTableRows(newData, config, allocationType)}
						</tbody>
					</table>
					<Disclaimer appParams={appParams} disclaimer={shareClass.disclaimers[`breakdown_definition_${data[0].allocation_type}_${appParams.clienttype}_0`]}></Disclaimer>
					<br />
				</div>
			);
		} else {
			return (
				<div key={data[0].display_order * 103943} className={`${styles.colXs12} ${styles.colMd6}`}>
					<h4>{data[0].heading}</h4>
					<div className={styles.toggleButtonContainer}>
					{/* No toggle buttons on table but this is needed to fill the space*/}
					</div>
					<table className={styles.table}>
						<thead>
							<tr>
								{config.headings.map((item, index) => (
									<th key={index * 9}>{item}</th>
								))}
							</tr>
						</thead>
						<tbody>
							{this.getGroupedTableRows(newData, config, allocationType)}
						</tbody>
					</table>
					<Disclaimer appParams={appParams} disclaimer={shareClass.disclaimers[`breakdown_definition_${data[0].allocation_type}_${appParams.clienttype}_0`]}></Disclaimer>
					<br />
				</div>
			);
		}
	}

	getGroupedTableRows(data, config, allocationType) {
		let rows = [];
		if (data.total.length > 0) {
			data.total.map((totalItem, trIndex) => {
				rows.push(
					<DataTableRow
						key={`${totalItem.label}-${trIndex * 29343983}`}
						config={config}
						data={totalItem}
						styles={styles}
						isGroupHeading={true}>
					</DataTableRow>
				);
				let itemMatchTotal = data.item.filter(item => item["group_name"] === totalItem["group_name"]);
				if (data.total[0].allocation_type === "sector") { //SECTOR specific HACK
					itemMatchTotal.map((item, index) => {
						rows.push(
							<DataTableRow key={`${item.label}-${trIndex}`}
								config={config}
								data={item}
								styles={styles}
								isGroupHeading={false}>
							</DataTableRow>
						);
					});
				}
				else {
					if (itemMatchTotal.length >= 1 && itemMatchTotal[0].label.toLowerCase() !== totalItem.label.toLowerCase()) {
						itemMatchTotal.map((item, index) => {
							rows.push(
								<DataTableRow key={`${item.label}-${trIndex}`}
									config={config}
									data={item}
									styles={styles}
									isGroupHeading={false}>
								</DataTableRow>
							);
						});
					}
				}
			});
		} else {
			if (allocationType === "credit_rating") {
				data.item
					.filter(item => item.type !== "AVERAGE")
					.map((item, index) => {
						rows.push(this.addAverageItemToRow(item, index, config));
					});

				data.item
					.filter(item => item.type === "AVERAGE")
					.map((item, index) => {
						rows.push(this.addAverageItemToRow(item, index, config));
					});
			}
			else {
				data.item.map((item, index) => rows.push(this.addAverageItemToRow(item, index, config)));
			}
		}
		return rows;
	}

	addAverageItemToRow(item, index, config) {

		if (!isNullOrUndefined(item.value)) { item.value = parseToRoundedDecimal(item.value, 1); }
		if (!isNullOrUndefined(item.allocation_value)) { item.allocation_value = parseToRoundedDecimal(item.allocation_value, 1); }
		if (!isNullOrUndefined(item.long_exposure)) { item.long_exposure = parseToRoundedDecimal(item.long_exposure, 1); }
		if (!isNullOrUndefined(item.short_exposure)) { item.short_exposure = parseToRoundedDecimal(item.short_exposure, 1); }
		if (!isNullOrUndefined(item.relative_weight)) { item.relative_weight = parseToRoundedDecimal(item.relative_weight, 1); }

		return (
			<DataTableRow
				key={`${item.label}-${index}`}
				config={config}
				data={item}
				styles={styles}
				isGroupHeading={item.type === "AVERAGE"}>
			</DataTableRow>
		);
	}

	render() {
		let { shareClass, appParams } = this.props;
		let { displayTypes } = this.state;
		let breakdowns = this.getGroupedBreakdowns();
		let reportMonth = safe(() => breakdowns[Object.keys(breakdowns)[0]][0]["date"], moment());
		const allowedAdditionalViewKeys = ["physical", "cds long", "cds short", "short exposure", "long exposure"];

		return (
			<div className={styles.wrapper}>
				<div className={styles.allocationBreakdownContainer}>
					<BackToTop styles={backToTopstyles} />
					<div className={styles.row}>
						<div className={styles.colXs12}>
							<h2 className={styles.title + ' ' + styles.reasons}>Allocation and Breakdown</h2>
						</div>
					</div>
					<div className={styles.row}>
						<div className={styles.colXs12}>
							<div className={styles.monthPickerContainer}>
								<span>Figures as at {moment(reportMonth as any).format("MMMM YYYY")}</span>
							</div>
						</div>
					</div>
					<div className={styles.row}>
						{Object.keys(breakdowns).map((order, index) => {
							let allocationType = safe(() => breakdowns[order][0]["allocation_type"] as string, null);
							let tableHeadings = null;
							let tableHeadingString = safe(() => breakdowns[order][0]["table_headings"] as string, null);

							if (!isNullOrUndefined(tableHeadingString)) {
								tableHeadings = tableHeadingString.split("|");
							}
							if (!isNullOrUndefined(allocationType)) {
								if (displayTypes.verticalChart.indexOf(allocationType.toUpperCase()) >= 0) {
									const keys = tableHeadings.filter(key => allowedAdditionalViewKeys.includes(key.toLowerCase()));
									if ((allocationType === "geographic" || allocationType === "credit_rating" || allocationType === "asset")) {
										if (keys.length > 0) {
											 return this.getStackedChart(breakdowns[order], shareClass, appParams);
										}
										else {
											 return this.getVerticalBarChart(breakdowns[order], shareClass, appParams);
										}
									} else {
										return this.getVerticalBarChart(breakdowns[order], shareClass, appParams);
									}
								}
								if (displayTypes.table.indexOf(allocationType.toUpperCase()) >= 0) {
									return this.getDataTable(displayTypes.tableConfig[allocationType.toUpperCase()], breakdowns[order], shareClass, appParams, allocationType);
								}
								if (displayTypes.horizontalChart.indexOf(allocationType.toUpperCase()) >= 0) {
									return this.getHorizontalChart(breakdowns[order], shareClass, appParams);
								}
							}
						})}
					</div>
					{!isNullOrUndefined(shareClass.disclaimers["shareclass_allocationbreakdown"]) && !shareClass.disclaimers["shareclass_allocationbreakdown"][0].is_deleted &&
						<div className={styles.disclaimer}>
							<Disclaimer appParams={appParams} disclaimer={shareClass.disclaimers["shareclass_allocationbreakdown"]}></Disclaimer>
						</div>
					}
				</div> {/*ends the allocationBreakdownContainer */}
			</div>
		);
	}
}


// WEBPACK FOOTER //
// ./src/client/components/PageAllocations/index.tsx